/**
 * Procès-verbal de l'audience : chapitres par étape (les étapes passées se
 * replient), question → réponse du témoin et incident → décision regroupés,
 * voix de la Cour en serif. Les consignes du greffier (« La parole est à… »)
 * n'y figurent plus : l'en-tête de la salle dit qui a la parole.
 */
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { TrialAvatar, witnessVariant } from './Courtroom';
import {
  DEBATE_PHASES, SIDE_TEXT, formatTime, renderContent, stripSpeakerPrefix,
  type TrialMessage, type Witness,
} from './trialTypes';

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI'];

type Item =
  | { kind: 'qa'; q: TrialMessage; a: TrialMessage | null }
  | { kind: 'incident'; inc: TrialMessage; ruling: TrialMessage | null }
  | { kind: 'msg'; m: TrialMessage };

/** Regroupe question/réponse et incident/décision (premier suivant correspondant). */
function buildItems(messages: TrialMessage[]): Item[] {
  const used = new Set<string>();
  const items: Item[] = [];
  messages.forEach((m, i) => {
    if (used.has(m.id)) return;
    const isParty = m.role === 'prosecution' || m.role === 'defense';
    if (isParty && m.metadata?.witness_id && m.msg_type !== 'incident') {
      const a = messages.slice(i + 1).find(x =>
        !used.has(x.id) && x.role === 'witness' && x.metadata?.witness_id === m.metadata?.witness_id);
      if (a) used.add(a.id);
      items.push({ kind: 'qa', q: m, a: a ?? null });
      return;
    }
    if (isParty && m.msg_type === 'incident') {
      const r = messages.slice(i + 1).find(x => !used.has(x.id) && x.role === 'judge' && x.metadata?.incident);
      if (r) used.add(r.id);
      items.push({ kind: 'incident', inc: m, ruling: r ?? null });
      return;
    }
    items.push({ kind: 'msg', m });
  });
  return items;
}

function Body({ content, serif = false, className = '' }: { content: string; serif?: boolean; className?: string }) {
  return (
    <div
      className={`leading-relaxed whitespace-pre-wrap break-words text-foreground ${serif ? 'font-serif text-[15px] sm:text-base' : 'text-sm'} ${className}`}
      dangerouslySetInnerHTML={{ __html: renderContent(stripSpeakerPrefix(content)) }}
    />
  );
}

function Appear({ children }: { children: ReactNode }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 380, damping: 30, mass: 0.7 }}
    >
      {children}
    </motion.div>
  );
}

function Typing({ label }: { label: string }) {
  const reduce = useReducedMotion();
  return (
    <span className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground">
      {label}
      <span className="flex gap-1" aria-hidden="true">
        {[0, 1, 2].map(i => (
          <motion.span
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-current opacity-60"
            animate={reduce ? undefined : { y: [0, -4, 0] }}
            transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15, ease: 'easeInOut' }}
          />
        ))}
      </span>
    </span>
  );
}

function PartyHeader({ m, verb, target }: { m: TrialMessage; verb?: string; target?: string }) {
  return (
    <div className="flex items-center gap-2 text-xs min-w-0">
      <TrialAvatar role={m.role} size={28} />
      <span className={`font-bold truncate ${SIDE_TEXT[m.role]}`}>{m.speaker_name}</span>
      {verb && <span className="text-muted-foreground shrink-0">{verb}</span>}
      {target && <span className="font-bold text-foreground truncate">{target}</span>}
      <time dateTime={m.created_at} className="ml-auto shrink-0 text-muted-foreground tabular-nums">{formatTime(m.created_at)}</time>
    </div>
  );
}

function WitnessAnswer({ a, witness, witnesses }: { a: TrialMessage; witness?: Witness; witnesses: Witness[] }) {
  return (
    <div className="flex gap-3 px-4 py-3 bg-[#e9f1ec] dark:bg-[#1b2c25]/30">
      <TrialAvatar role="witness" variant={witnessVariant(a.metadata?.witness_id, witnesses)} size={36} />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-bold text-[#2c463c] dark:text-[#9cbfae]">
          {a.speaker_name}{witness?.role ? <span className="font-normal text-[#3d5f52]/80 dark:text-[#9cbfae]/80">, {witness.role}</span> : null}
        </div>
        <Body content={a.content} className="mt-1" />
      </div>
    </div>
  );
}

function ItemView({
  item, witnesses, pendingWitness,
}: { item: Item; witnesses: Witness[]; pendingWitness: boolean }) {
  const { t } = useTranslation();
  const card = 'rounded-2xl border border-border bg-card overflow-hidden';

  if (item.kind === 'qa') {
    const w = witnesses.find(x => x.id === item.q.metadata?.witness_id);
    return (
      <article className={card}>
        <div className="px-4 py-3">
          <PartyHeader m={item.q} verb={t('tribunal.court.questions_verb')} target={w?.name ?? item.a?.speaker_name} />
          <Body content={item.q.content} className="mt-1.5 sm:pl-9" />
        </div>
        {item.a ? (
          <div className="border-t border-[#d8e6de] dark:border-[#2c463c]"><WitnessAnswer a={item.a} witness={w} witnesses={witnesses} /></div>
        ) : pendingWitness ? (
          <div className="border-t border-border px-4 py-2.5"><Typing label={t('tribunal.pending.witness')} /></div>
        ) : null}
      </article>
    );
  }

  if (item.kind === 'incident') {
    return (
      <article className="rounded-2xl border border-[#e2c3b5] dark:border-[#5e2018] bg-card overflow-hidden">
        <div className="px-4 py-3 bg-[#f6e9e3] dark:bg-[#3a150f]/30">
          <div className="flex items-center gap-2 text-xs">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#9b3a2e] dark:text-[#c47a68] shrink-0" aria-hidden="true"><path d="M12 3l9 16H3z" /><path d="M12 10v4M12 17h.01" /></svg>
            <span className="font-bold text-[#9b3a2e] dark:text-[#c47a68]">{t('tribunal.court.incident_by', { name: item.inc.speaker_name })}</span>
            <time dateTime={item.inc.created_at} className="ml-auto text-muted-foreground tabular-nums">{formatTime(item.inc.created_at)}</time>
          </div>
          <Body content={item.inc.content} className="mt-1.5" />
        </div>
        {item.ruling && (
          <div className="flex gap-3 px-4 py-3 border-t border-[#e2c3b5] dark:border-[#5e2018]">
            <TrialAvatar role="judge" size={32} />
            <div className="min-w-0">
              <div className="text-xs font-bold text-[#6e4a1a] dark:text-[#e0b866]">{t('tribunal.court.ruling_label')}</div>
              <Body content={item.ruling.content} serif className="mt-1" />
            </div>
          </div>
        )}
      </article>
    );
  }

  const m = item.m;
  if (m.role === 'judge') {
    return (
      <article className="flex gap-3.5 rounded-2xl bg-[#f8eedb] dark:bg-[#33220b]/25 px-4 py-4">
        <TrialAvatar role="judge" size={44} />
        <div className="min-w-0 flex-1">
          <div className="flex gap-2 text-xs">
            <span className="font-bold text-[#6e4a1a] dark:text-[#e0b866]">{m.speaker_name || t('tribunal.roles.president')}</span>
            <time dateTime={m.created_at} className="text-muted-foreground tabular-nums">{formatTime(m.created_at)}</time>
          </div>
          <Body content={m.content} serif className="mt-1" />
        </div>
      </article>
    );
  }
  if (m.role === 'clerk' || m.role === 'system') {
    return (
      <div className="flex items-start justify-center gap-2 px-4 text-center">
        {m.role === 'clerk' && <TrialAvatar role="clerk" size={22} />}
        <p className="text-xs italic text-muted-foreground leading-relaxed max-w-xl">{stripSpeakerPrefix(m.content)}</p>
      </div>
    );
  }
  if (m.role === 'witness') {
    return <article className={card}><WitnessAnswer a={m} witness={witnesses.find(x => x.id === m.metadata?.witness_id)} witnesses={witnesses} /></article>;
  }
  // Avocat : plaidoirie, pièce versée
  const isEvidence = m.msg_type === 'evidence';
  return (
    <article className={card}>
      <div className="px-4 py-3">
        <PartyHeader m={m} verb={isEvidence ? t('tribunal.court.files_evidence') : undefined} />
        <Body content={m.content} className="mt-1.5 sm:pl-9" />
      </div>
    </article>
  );
}

export function Transcript({
  messages, witnesses, currentPhase, status, pendingActor, footer,
}: {
  messages: TrialMessage[];
  witnesses: Witness[];
  currentPhase: string;
  status: string;
  pendingActor: 'judge' | 'witness' | null;
  /** Verdict mis en scène, en fin de procès-verbal. */
  footer?: ReactNode;
}) {
  const { t } = useTranslation();
  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const visible = messages.filter(m => m.msg_type !== 'cue');

  // Chapitres par étape, dans l'ordre d'apparition
  const chapters: { phase: string; msgs: TrialMessage[] }[] = [];
  for (const m of visible) {
    const last = chapters[chapters.length - 1];
    if (last && last.phase === m.phase) last.msgs.push(m);
    else chapters.push({ phase: m.phase, msgs: [m] });
  }
  const curIdx = DEBATE_PHASES.indexOf(currentPhase);
  const lastWitnessQuestion = [...visible].reverse().find(m => m.metadata?.witness_id && m.role !== 'witness');
  const lastAnswered = lastWitnessQuestion
    ? visible.some(m => m.role === 'witness' && m.created_at > lastWitnessQuestion.created_at)
    : true;

  return (
    <div className="flex flex-col gap-3.5">
      {chapters.map((ch, ci) => {
        const idx = DEBATE_PHASES.indexOf(ch.phase);
        // La phase qui vient de se clore reste ouverte : l'avocat dont la
        // plaidoirie a déclenché le passage à l'étape suivante doit la voir.
        const past = status === 'in_progress' && idx >= 0 && idx < curIdx - 1;
        const collapsed = toggled[`${ch.phase}-${ci}`] ?? past;
        const parties = ch.msgs.filter(m => m.role === 'prosecution' || m.role === 'defense').length;
        const title = `${idx >= 0 ? `${ROMAN[idx]}. ` : ''}${t(`tribunal.phases_long.${ch.phase}`, ch.phase)}`;
        const isLastChapter = ci === chapters.length - 1;
        return (
          <section key={`${ch.phase}-${ci}`} aria-label={title} className="flex flex-col gap-3.5">
            <button
              type="button"
              onClick={() => setToggled(s => ({ ...s, [`${ch.phase}-${ci}`]: !collapsed }))}
              aria-expanded={!collapsed}
              className={`flex items-center gap-3 w-full text-left rounded-xl ${collapsed ? 'border border-border bg-card px-4 py-3' : 'pt-2'}`}
            >
              <span className="font-serif font-semibold text-base sm:text-lg text-foreground shrink-0">{title}</span>
              {collapsed
                ? <span className="text-xs text-muted-foreground truncate">{t('tribunal.court.chapter_count', { count: parties })}</span>
                : <span className="flex-1 h-px bg-border" aria-hidden="true" />}
              <svg className={`ml-auto shrink-0 text-muted-foreground transition-transform ${collapsed ? '' : 'rotate-180'}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
            </button>
            {!collapsed && buildItems(ch.msgs).map(item => (
              <Appear key={item.kind === 'qa' ? item.q.id : item.kind === 'incident' ? item.inc.id : item.m.id}>
                <ItemView
                  item={item}
                  witnesses={witnesses}
                  pendingWitness={isLastChapter && pendingActor === 'witness' && !lastAnswered}
                />
              </Appear>
            ))}
          </section>
        );
      })}

      <AnimatePresence>
        {pendingActor === 'judge' && (
          <motion.div
            key="pending-judge"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-3 rounded-2xl bg-[#f8eedb] dark:bg-[#33220b]/25 px-4 py-3"
          >
            <TrialAvatar role="judge" size={32} thinking />
            <Typing label={t('tribunal.pending.judge', { name: t('tribunal.roles.president') })} />
          </motion.div>
        )}
        {pendingActor === 'witness' && lastAnswered && (
          <motion.div
            key="pending-witness"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-3 rounded-2xl bg-[#e9f1ec] dark:bg-[#1b2c25]/30 px-4 py-3"
          >
            <TrialAvatar role="witness" size={32} thinking />
            <Typing label={t('tribunal.pending.witness')} />
          </motion.div>
        )}
      </AnimatePresence>

      {footer}
    </div>
  );
}
